'use client'

import {
  Bot,
  Menu,
  Package2,
  Users,
  MessageSquare,
  FileText,
  Settings,
  Facebook,
  Instagram,
  Smartphone,
  Building,
  Award,
  File,
  Contact,
  LayoutDashboard,
  Briefcase,
  GitBranch,
  UserCheck,
  Workflow,
  Cpu,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Search,
  X,
  MapPin,
  ShoppingCart,
  Send,
  Mic,
  MessageCircle,
  Share2,
  Database,
  BarChart3,
  Globe,
  HeadphonesIcon,
  Waves,
  Wrench,
  Shield,
  KeyRound,
  Coins,
  FileBarChart,
  Wallet,
  UserPlus,
  ScrollText,
  Calendar,
  Banknote,
  Server,
  Gift,
  BadgePercent,
  Brain,
  FlaskConical,
  User,
  LogOut,
} from 'lucide-react'
import { Link, usePathname, useRouter } from '@/i18n/navigation'
import { useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { useAdminAuth } from '@/stores/auth'
import { useSidebarState } from '@/stores/ui'
import { routing } from '@/i18n/routing'

const PUBLIC_ADMIN_ROUTES = new Set<string>([
  '/admin',
  '/admin/reset-password',
  '/admin/reset-password/request',
])

function stripLocalePrefix(path: string | null) {
  if (!path) return '/'
  for (const locale of routing.locales) {
    const prefix = `/${locale}`
    if (path === prefix) {
      return '/'
    }
    if (path.startsWith(`${prefix}/`)) {
      return path.slice(prefix.length) || '/'
    }
  }
  return path
}

function normalizePathname(path: string | null) {
  const stripped = stripLocalePrefix(path)
  if (!stripped) return '/'
  if (stripped.length > 1 && stripped.endsWith('/')) {
    return stripped.slice(0, -1)
  }
  return stripped
}

export default function AdminLayout({
  children,
}: {
  children: React.ReactNode
}) {
  const pathname = usePathname()
  const { isAuthenticated, verify, isLoading, user, logout } = useAdminAuth()
  const {
    sidebarCollapsed,
    toggleSidebar,
    ensureAdminSidebarCollapsed,
    hasHydrated,
  } = useSidebarState()
  const [authChecked, setAuthChecked] = useState(false)
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({})
  const normalizedPath = normalizePathname(pathname)
  const collapsedSidebar = hasHydrated ? sidebarCollapsed : true

  const adminNavSections = useMemo(
    () => [
      {
        title: 'Main',
        items: [
          {
            href: '/admin/dashboard',
            label: 'Dashboard',
            icon: LayoutDashboard,
          },
        ],
      },
      {
        title: 'Reports',
        items: [
          {
            href: '/admin/reports/transactions',
            label: 'Transaction Analytics',
            icon: BarChart3,
          },
          {
            href: '/admin/reports/wallet-journals',
            label: 'Wallet Journal Reports',
            icon: ScrollText,
          },
        ],
      },
      {
        title: 'AI & Automation',
        items: [
          {
            href: '/admin/bots',
            label: 'Bots',
            icon: Bot,
          },
          {
            href: '/admin/ai-tools',
            label: 'Bot Tools',
            icon: Wrench,
          },
          {
            href: '/admin/flows',
            label: 'Flows',
            icon: Workflow,
          },
          {
            href: '/admin/ai-process-orchestrator',
            label: 'AI Process Orchestrator',
            icon: Cpu,
          },
          {
            href: '/admin/playground',
            label: 'LLM Playground',
            icon: FlaskConical,
          },
        ],
      },

      {
        title: 'Knowledge Management',
        items: [
          {
            href: '/admin/collections',
            label: 'Document Collections',
            icon: FileText,
          },
          {
            href: '/admin/documents',
            label: 'Documents',
            icon: File,
          },
          {
            href: '/admin/knowledge-search',
            label: 'Knowledge Search',
            icon: Search,
          },
          {
            href: '/admin/knowledge-skills',
            label: 'Knowledge Skills',
            icon: Brain,
          },
        ],
      },
      {
        title: 'Healthcare',
        items: [
          {
            href: '/admin/appointment',
            label: 'Appointment',
            icon: Calendar,
          },
        ],
      },
      {
        title: 'Support & Tickets',
        items: [
          {
            href: '/admin/tickets',
            label: 'Tickets',
            icon: HeadphonesIcon,
          },
          {
            href: '/admin/tickets/categories',
            label: 'Ticket Categories',
            icon: GitBranch,
          },
        ],
      },
      {
        title: 'E-commerce',
        items: [
          {
            href: '/admin/products',
            label: 'Products',
            icon: Package2,
          },
          {
            href: '/admin/customers',
            label: 'Customers',
            icon: Users,
          },
          {
            href: '/admin/customer-addresses',
            label: 'Customer Addresses',
            icon: MapPin,
          },
          {
            href: '/admin/orders',
            label: 'Orders',
            icon: ShoppingCart,
          },
          {
            href: '/admin/order-tracking',
            label: 'Order Tracking',
            icon: Package2,
          },
        ],
      },
      {
        title: 'Social Media & Communication',
        items: [
          {
            href: '/admin/contact',
            label: 'Contacts',
            icon: Contact,
          },
          {
            href: '/admin/facebook',
            label: 'Facebook',
            icon: Facebook,
          },
          {
            href: '/admin/instagram',
            label: 'Instagram',
            icon: Instagram,
          },
          {
            href: '/admin/telegram',
            label: 'Telegram',
            icon: Send,
          },
          // {
          //   href: '/admin/elevenlabs',
          //   label: 'ElevenLabs',
          //   icon: Mic,
          // },
          {
            href: '/admin/voice-presets',
            label: 'Realtime Voice',
            icon: Waves,
          },
          {
            href: '/admin/twilio-whatsapp',
            label: 'Twilio WhatsApp',
            icon: Smartphone,
          },
          {
            href: '/admin/fb-whatsapp',
            label: 'WhatsApp',
            icon: MessageCircle,
          },
        ],
      },
      {
        title: 'LLM & Billing',
        items: [
          {
            href: '/admin/llm/pricing',
            label: 'LLM Pricing',
            icon: Coins,
          },
          {
            href: '/admin/llm/usage',
            label: 'Usage Logs',
            icon: FileBarChart,
          },
        ],
      },
      {
        title: 'Wallet Management',
        items: [
          {
            href: '/admin/wallets',
            label: 'Wallets',
            icon: Wallet,
          },
          {
            href: '/admin/wallet-providers',
            label: 'Wallet Providers',
            icon: Server,
          },
          {
            href: '/admin/user-wallets',
            label: 'User Wallets',
            icon: UserPlus,
          },
          {
            href: '/admin/wallet-journals',
            label: 'Wallet Journals',
            icon: ScrollText,
          },
          {
            href: '/admin/payouts',
            label: 'Payout Requests',
            icon: Banknote,
          },
          {
            href: '/admin/rewards',
            label: 'Rewards',
            icon: Gift,
          },
          {
            href: '/admin/bonus-promotions',
            label: 'Bonus Promotions',
            icon: BadgePercent,
          },
          {
            href: '/admin/active-bonuses',
            label: 'Active Bonuses',
            icon: Award,
          },
          {
            href: '/admin/bonus-agents',
            label: 'Bonus Agents',
            icon: Coins,
          },
          {
            href: '/admin/bonus-agent-journals',
            label: 'Bonus Agent Journals',
            icon: ScrollText,
          },
          {
            href: '/admin/bonus-journals',
            label: 'Bonus Journals',
            icon: ScrollText,
          },
          {
            href: '/admin/croco-transactions',
            label: 'Croco Transactions',
            icon: ScrollText,
          },
        ],
      },
      {
        title: 'User Management',
        items: [
          {
            href: '/admin/users',
            label: 'Users',
            icon: Users,
          },
          {
            href: '/admin/agents',
            label: 'Agents',
            icon: Briefcase,
          },
          {
            href: '/admin/teams',
            label: 'Teams',
            icon: Shield,
          },
          {
            href: '/admin/permissions',
            label: 'Permissions',
            icon: KeyRound,
          },
        ],
      },
      {
        title: 'System Configuration',
        items: [
          {
            href: '/admin/flows/modules',
            label: 'Flow Modules',
            icon: GitBranch,
          },
          {
            href: '/admin/erpnext-configurations',
            label: 'ERPNext Configurations',
            icon: Settings,
          },
          {
            href: '/admin/site-configuration',
            label: 'Site Configuration',
            icon: Globe,
          },
          {
            href: '/admin/s3-configurations',
            label: 'S3 Configurations',
            icon: Settings,
          },
          {
            href: '/admin/services',
            label: 'Services',
            icon: GitBranch,
          },
        ],
      },
    ],
    []
  )

  const [searchQuery, setSearchQuery] = useState('')

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return adminNavSections

    const query = searchQuery.toLowerCase()
    return adminNavSections
      .map(section => ({
        ...section,
        items: section.items.filter(item =>
          item.label.toLowerCase().includes(query)
        ),
      }))
      .filter(section => section.items.length > 0)
  }, [searchQuery, adminNavSections])

  const activeNavHref = useMemo(() => {
    const allItems = filteredSections.flatMap(section => section.items)
    const matches = allItems.filter(item => {
      if (
        item.href.endsWith('/dashboard') &&
        (normalizedPath === '/admin' || normalizedPath === '/admin/')
      ) {
        return true
      }
      return (
        normalizedPath === item.href ||
        normalizedPath.startsWith(item.href + '/')
      )
    })
    // Sort by length desc => longest match wins (e.g. /tickets/categories > /tickets)
    return matches.sort((a, b) => b.href.length - a.href.length)[0]?.href
  }, [filteredSections, normalizedPath])

  useEffect(() => {
    if (!hasHydrated) return
    ensureAdminSidebarCollapsed()
  }, [ensureAdminSidebarCollapsed, hasHydrated])

  // Auto-expand section containing current route, collapse others
  useEffect(() => {
    // When searching, auto-expand all sections that have matches
    if (searchQuery.trim()) {
      const allExpanded: Record<string, boolean> = {}
      filteredSections.forEach(section => {
        allExpanded[section.title] = true
      })
      setExpandedSections(allExpanded)
      return
    }

    let activeSection = filteredSections.find(section =>
      section.items.some(item => item.href === activeNavHref)
    )

    // If on admin root, default to Main section
    if (!activeSection && normalizedPath === '/admin') {
      activeSection = filteredSections.find(section => section.title === 'Main')
    }

    if (activeSection) {
      setExpandedSections(() => {
        // Collapse all sections except the active one
        const newExpanded: Record<string, boolean> = {}
        newExpanded[activeSection.title] = true
        return newExpanded
      })
    }
  }, [filteredSections, activeNavHref, normalizedPath, searchQuery])

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const renderUserFooter = (collapsed: boolean) => (
    <div className="border-t border-gray-700 px-4 py-3">
      {collapsed ? (
        <div className="flex flex-col items-center gap-2">
          <Link
            href="/admin/profile"
            className="flex items-center justify-center rounded-lg p-2 text-gray-300 hover:text-white hover:bg-gray-700"
            title={user?.username || 'Profile'}
          >
            <User className="h-4 w-4" />
          </Link>
          <button
            onClick={logout}
            className="flex items-center justify-center rounded-lg p-2 text-gray-300 hover:text-white hover:bg-gray-700"
            title="Logout"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <Link
            href="/admin/profile"
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-700 min-w-0"
          >
            <User className="h-4 w-4 shrink-0" />
            <span className="truncate">{user?.username || 'Profile'}</span>
          </Link>
          <button
            onClick={logout}
            className="flex items-center rounded-lg p-1.5 text-gray-300 hover:text-white hover:bg-gray-700"
            title="Logout"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )

  const renderNavLinks = (isCollapsed = collapsedSidebar) => {
    if (isCollapsed) {
      // Collapsed view: show all items without grouping
      return filteredSections.flatMap(section =>
        section.items.map(item => (
          <Link
            key={`${section.title}-${item.href}`}
            href={item.href}
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-gray-300 transition-all hover:text-white hover:bg-gray-700 justify-center',
              {
                'bg-blue-600 text-white hover:bg-blue-700':
                  activeNavHref === item.href,
              }
            )}
            title={item.label}
          >
            <item.icon className="h-4 w-4" />
          </Link>
        ))
      )
    }

    // Expanded view: show grouped items with collapsible sections
    return filteredSections.map(section => {
      const isExpanded = searchQuery.trim()
        ? true
        : expandedSections[section.title] === true // Only expand if explicitly set
      const hasActiveItem = section.items.some(
        item => item.href === activeNavHref
      )

      return (
        <div key={section.title} className="mb-2">
          <button
            onClick={() => toggleSection(section.title)}
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-gray-300 transition-all hover:text-white hover:bg-gray-700 w-full text-left',
              {
                'bg-gray-700 text-white': hasActiveItem,
              }
            )}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
            <span className="font-medium text-sm">{section.title}</span>
          </button>
          {isExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {section.items.map(item => (
                <Link
                  key={`${section.title}-${item.href}`}
                  href={item.href}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-gray-300 transition-all hover:text-white hover:bg-gray-700',
                    {
                      'bg-blue-600 text-white hover:bg-blue-700':
                        activeNavHref === item.href,
                    }
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )
    })
  }

  const router = useRouter()

  // Ensure we initialise/verify auth once on mount so we don't redirect
  // before the store has had a chance to rehydrate or verify the token.
  useEffect(() => {
    let mounted = true

    const init = async () => {
      // Wait a brief moment to allow for store rehydration from localStorage
      await new Promise(resolve => setTimeout(resolve, 0))

      try {
        // Call verify() which will return quickly if there's no token.
        await verify()
      } catch (e) {
        // verification failed or network error - we'll treat as unauthenticated
      } finally {
        if (mounted) setAuthChecked(true)
      }
    }

    init()

    return () => {
      mounted = false
    }
  }, [verify])

  useEffect(() => {
    // Wait until we've attempted to initialise/verify auth before redirecting.
    // This prevents an immediate redirect when the zustand store is still
    // rehydrating from localStorage (which would temporarily show unauthenticated).
    if (!authChecked) return

    // If we're still performing an explicit login/verify network call, don't
    // redirect just yet either.
    if (isLoading) return

    // If not authenticated and on a protected route, redirect to the admin login
    if (
      !isAuthenticated &&
      normalizedPath &&
      !PUBLIC_ADMIN_ROUTES.has(normalizedPath)
    ) {
      const loginPath = `/admin`
      // Avoid redirect loop when already on the login page
      router.replace(loginPath)
    }
  }, [authChecked, isAuthenticated, isLoading, normalizedPath, router])

  return (
    <div
      className={cn(
        'grid min-h-screen w-full overflow-x-hidden',
        collapsedSidebar
          ? 'lg:grid-cols-[80px_1fr]'
          : 'lg:grid-cols-[280px_1fr]'
      )}
    >
      <div className="hidden border-r bg-gray-800 lg:block dark:bg-gray-900">
        <div className="flex h-full flex-col gap-2 text-center ">
          <div className="flex h-[60px] items-center justify-between border-b border-gray-700 px-4">
            <div
              className={cn(
                'flex w-full',
                collapsedSidebar ? 'justify-center' : 'justify-start'
              )}
            >
              <Link
                href="/admin/dashboard"
                className={cn(
                  'flex items-center gap-2 font-semibold text-white transition-all duration-200',
                  collapsedSidebar ? 'justify-center w-full' : ''
                )}
              >
                <Package2 className="h-6 w-6" />
                {!collapsedSidebar && <span>Admin Dashboard</span>}
              </Link>
            </div>
          </div>
          <div className="flex-1 overflow-auto py-2">
            {!collapsedSidebar && (
              <div className="px-4 pb-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Filter menu..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="h-8 bg-gray-700 border-gray-600 pl-8 pr-8 text-sm text-gray-200 placeholder:text-gray-500 focus-visible:ring-gray-500 focus-visible:ring-1"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}
            <nav
              className={cn(
                'grid items-start px-4 text-sm font-medium transition-all duration-200',
                collapsedSidebar ? 'space-y-2' : 'space-y-1'
              )}
            >
              {isAuthenticated && renderNavLinks()}
            </nav>
          </div>
          {isAuthenticated && renderUserFooter(collapsedSidebar)}
        </div>
      </div>
      <div className="flex flex-col min-w-0 w-full">
        <header className="flex h-12 items-center gap-4 border-b bg-gray-800 px-6 dark:bg-gray-900 lg:h-12">
          {isAuthenticated && (
            <>
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0 lg:hidden border-gray-600 text-white hover:bg-gray-700"
                  >
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Toggle navigation menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="left"
                  className="flex h-full w-[280px] flex-col bg-gray-800 text-white"
                >
                  <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                  <nav className="flex-1 overflow-y-auto px-4 pt-8 text-lg font-medium">
                    <Link
                      href="/admin/dashboard"
                      className="flex items-center gap-2 text-lg font-semibold mb-4 text-white"
                    >
                      <Package2 className="h-6 w-6" />
                      <span>Admin Dashboard</span>
                    </Link>
                    {renderNavLinks(false)}
                  </nav>
                  {renderUserFooter(false)}
                </SheetContent>
              </Sheet>
              <Button
                variant="ghost"
                size="icon"
                className="hidden lg:flex h-8 w-8 text-white hover:bg-gray-700"
                onClick={toggleSidebar}
                title={collapsedSidebar ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsedSidebar ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronLeft className="h-4 w-4" />
                )}
              </Button>
            </>
          )}
          <div className="w-full flex-1">
            {/* Can add breadcrumbs or other header content here */}
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden p-4 sm:p-6">{children}</main>
      </div>
    </div>
  )
}
